/* eslint-disable react/jsx-pascal-case */
import React from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Highcharts from "highcharts";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HighchartsReact from "highcharts-react-official";
import NavBar from "../../../components/navbar/navbar";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ActionTypes,
  ClickOnPlay,
  FiltersState,
  Log,
  LogResponse,
  topOffer,
  topZipCode,
  TotalEvento,
} from "./types";
import FiltersReducer from "./reducer";
import { API_URL, colors, verify } from "../../../utils/common";
import { FiltersContainer, InfoContainer, TotalsCardsContainer } from "./styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import swal from "sweetalert";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import StatisticCard from "../../../components/statisticCard.tsx/statisticCard";
import { OfferRow } from "../offers/types";

const initialFiltersState: FiltersState = {
  dayFrom: dayjs(new Date()),
  dayTo: dayjs(new Date()),
};

const Statistics: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const [filtersState, dispatch] = React.useReducer(
    FiltersReducer,
    initialFiltersState
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isDownloading, setIsDownloading] = React.useState<boolean>(false);
  const [totalEvents, setTotalEvents] = React.useState<TotalEvento[]>([]);
  const [clickOnPlay, setClickOnPlay ] = React.useState<ClickOnPlay[]>([]);
  const [data, setData] = React.useState<Log[]>([]);
  const [rowCount, setRowCount] = React.useState<number>(0);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [topZipCodes, setTopZipCodes] = React.useState<topZipCode[]>([]);
  const [topOffer, setTopOffer] = React.useState<OfferRow | undefined>(undefined);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const columns = React.useMemo<MRT_ColumnDef<Log>[]>(
    () => [
      {
        accessorKey: "usuario",
        header: "Usuario",
      },
      {
        accessorKey: "evento",
        header: "Evento",
      },
      {
        accessorKey: "descripcion",
        header: "Descripción",
      },
      {
        accessorKey: "fechacreacion",
        header: "Fecha",
      },
    ],
    []
  );

  const getEvents = React.useCallback(() => {
    const token = localStorage.getItem("jwt");
    const params = new URLSearchParams({
      dayFrom: filtersState.dayFrom.format("YYYY-MM-DD"),
      dayTo: filtersState.dayTo.format("YYYY-MM-DD"),
      reportType: "TotalEventos",
    });
    fetch(`${API_URL}/reports?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: TotalEvento[]) => {
        setIsLoading(false);
        setTotalEvents(response);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [filtersState]);

  const getLogs = React.useCallback(() => {
    const token = localStorage.getItem("jwt");
    const params = new URLSearchParams({
      dayFrom: filtersState.dayFrom.format("YYYY-MM-DD"),
      dayTo: filtersState.dayTo.format("YYYY-MM-DD"),
      page: pagination.pageIndex.toString(),
      size: pagination.pageSize.toString(),
      evento: searchValue || "",
    });
    fetch(`${API_URL}/logs?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: LogResponse) => {
        setIsLoading(false);
        setData(response.content);
        setRowCount(response.totalElements);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [filtersState, pagination, searchValue]);

  const getMoreSearchedZips = React.useCallback(() => {
    const token = localStorage.getItem("jwt");
    const params = new URLSearchParams({
      dayFrom: filtersState.dayFrom.format("YYYY-MM-DD"),
      dayTo: filtersState.dayTo.format("YYYY-MM-DD"),
      limit: "5"
    });
    fetch(`${API_URL}/reports/top-codigos-postales?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: topZipCode[]) => {
        setIsLoading(false);
        setTopZipCodes(response)
      })
      .catch((err) => {
        console.error(err);
      });
  },[filtersState.dayFrom, filtersState.dayTo]);

  const getMoreSearchedOffers = React.useCallback(() => {
    const token = localStorage.getItem("jwt");
    const params = new URLSearchParams({
      dayFrom: filtersState.dayFrom.format("YYYY-MM-DD"),
      dayTo: filtersState.dayTo.format("YYYY-MM-DD"),
      limit: "1"
    });
    fetch(`${API_URL}/reports/top-ofertas?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: topOffer[]) => {
        setIsLoading(false);
        if (response.length){
          fetch(`${API_URL}/ofertas/${response[0].idOferta}`, {
              method: "GET",
              headers: {
                Authorization: `bearer ${token}`,
                "content-type": "application/json",
              },
            })
          .then((res) => res.json())
          .then((responseOffer: OfferRow) => {
            setIsLoading(false);
            setTopOffer(responseOffer);
          })
          .catch((err) => {
            console.error(err);
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  },[filtersState.dayFrom, filtersState.dayTo]);

  const getClickOnPlays = React.useCallback(() => {
    const token = localStorage.getItem("jwt");
    const params = new URLSearchParams({
      dayFrom: filtersState.dayFrom.format("YYYY-MM-DD"),
      dayTo: filtersState.dayTo.format("YYYY-MM-DD"),
    });
    fetch(`${API_URL}/reports/click-on-play?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: ClickOnPlay[]) => {
        setIsLoading(false);
        setClickOnPlay(response);
      })
      .catch((err) => {
        console.error(err);
      });
  },[filtersState.dayFrom, filtersState.dayTo])

  const playEventsChart = React.useMemo(
    () => ({
      chart: {
        type: "pie",
        width: 300,
        height: 225,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: "Total de clics en plays",
        align: "center",
        style: {
          fontSize: "15px",
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          borderWidth: 0,
          colorByPoint: true,
          type: "pie",
          size: "100%",
          innerSize: "70%",
          dataLabels: {
            enabled: false,
            crop: false,
            distance: "-10%",
            style: {
              fontWeight: "normal",
              fontSize: "10px",
            },
            connectorWidth: 0,
          },
        },
      },
      subtitle: {
        text: clickOnPlay.reduce(
          (accumulator, currentValue) =>
            accumulator + currentValue.total,
          0
        ),
        floating: true,
        verticalAlign: "middle",
        style: {
          fontSize: "18px",
          fontWeight: "normal",
          color: "black",
        },
        y: 35,
      },
      series: [
        {
          type: "pie",
          name: "Eventos",
          data: clickOnPlay.map((item) => [item.nombre, item.total]),
        },
      ],
    }),
    [clickOnPlay]
  );

  const totalEventsChart = React.useMemo(
    () => ({
      chart: {
        type: "pie",
        width: 300,
        height: 225,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: "Total de Eventos",
        align: "center",
        style: {
          fontSize: "15px",
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          borderWidth: 0,
          colorByPoint: true,
          type: "pie",
          size: "100%",
          innerSize: "70%",
          dataLabels: {
            enabled: false,
            crop: false,
            distance: "-10%",
            style: {
              fontWeight: "normal",
              fontSize: "10px",
            },
            connectorWidth: 0,
          },
        },
      },
      subtitle: {
        text: totalEvents.reduce(
          (accumulator, currentValue) =>
            accumulator + currentValue.totalEventos,
          0
        ),
        floating: true,
        verticalAlign: "middle",
        style: {
          fontSize: "18px",
          fontWeight: "normal",
          color: "black",
        },
        y: 35,
      },
      series: [
        {
          type: "pie",
          name: "Eventos",
          data: totalEvents.map((item) => [item.evento, item.totalEventos]),
        },
      ],
    }),
    [totalEvents]
  );

  const mostSearchedZips = React.useMemo(() => ({
        chart: {
            type: "bar",
            width: null
        },
        colors: ['#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F', '#EDC948'],
        credits: {
            enabled: false,
        },
        title: {
            text: "Códigos postales mas buscados",
            align: "center",
            style: {
                fontSize: "15px",
            },
        },
        xAxis: {
            categories: topZipCodes.map((item) => item.codigoPostal),
            title: {
                text: null
            },
            gridLineWidth: 1,
            lineWidth: 0
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Veces buscado'
            }
        },
        plotOptions: {
            bar: {
                colorByPoint: true,
                borderRadius: '50%',
                dataLabels: {
                    enabled: true
                },
                groupPadding: 0.1
            }
        },
        legend: {
            enabled: false,
        },
        series: [
            {
                type: "bar",
                name: "Veces Buscado",
                data: topZipCodes.map((item) => item.total),
            }
        ]
    }),[topZipCodes]);

  const handleInputChange = (value: any, type: ActionTypes) => {
    let date: string | undefined = undefined;
    dispatch({ type: type, payload: date || value });
  };

  const handleDownload = () => {
    setIsDownloading(true);
    const token = localStorage.getItem("jwt");
    const params = new URLSearchParams({
      dayFrom: filtersState.dayFrom.format("YYYY-MM-DD"),
      dayTo: filtersState.dayTo.format("YYYY-MM-DD"),
      page: pagination.pageIndex.toString(),
      size: rowCount.toString(),
      evento: searchValue || "",
    });
    fetch(`${API_URL}/logs?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: LogResponse) => {
        setIsDownloading(false);
        const worksheet = XLSX.utils.json_to_sheet(response.content);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, "Logs.xlsx");
      })
      .catch((err) => {
        swal({title: "Error :(", text: "Hubo un error favor de contactar a soporte", icon: "error"});
        console.error(err);
        setIsDownloading(false);
      });
  };

  React.useEffect(() => {
      (async () => {
        if (!["/", "/login"].includes(location.pathname)) {
          const currentToken = localStorage.getItem("jwt");
          if (currentToken) {
            const isValid = await verify(currentToken);
            if (!isValid) {
              navigate("/");
            } else {
              const permissions = JSON.parse(
                localStorage.getItem("user") || "{}"
              );
              if (!permissions?.administrationAccess) {
                navigate("/Home");
              }
            }
          } else {
            navigate("/");
          }
        }
      })();
    }, [location.pathname, navigate]);

  React.useEffect(() => {
    getEvents();
    getLogs();
    getMoreSearchedZips();
    getMoreSearchedOffers();
    getClickOnPlays();
  }, [getClickOnPlays, getEvents, getLogs, getMoreSearchedOffers, getMoreSearchedZips]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableFilters: true,
    enableHiding: false,
    enableSorting: false,
    enableColumnActions: false,
    enablePagination: true,
    enableRowActions: false,
    manualPagination: true,
    rowCount,
    manualFiltering: true,
    positionActionsColumn: "last",
    paginationDisplayMode: "pages",
    onPaginationChange: setPagination,
    onGlobalFilterChange: setSearchValue,
    renderToolbarInternalActions: ({ table }) => (
      <>
        <Tooltip title="Descargar Excel">
          {isDownloading ? (
            <CircularProgress size="30px" color="secondary" />
          ) : (
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          )}
        </Tooltip>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </>
    ),
    initialState: {
      showGlobalFilter: true,
    },
    state: {
      isLoading,
      pagination,
      globalFilter: searchValue,
    },
    localization: {
      search: "Busqueda por Evento",
      toggleFullScreen: "Modo Pantalla Completa",
      showHideColumns: "Mostrar/Ocultar Columnas",
      toggleDensity: "Cambiar ancho de filas"
    },
    muiTableContainerProps: {
      sx: { maxHeight: '700px' } 
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        "& td": {
          "&::after": {
            backgroundColor: "#f5f5f5!important",
          },
        },
        "&:hover": {
          "& td": {
            "&::after": {
              backgroundColor: "#0000004d!important",
            },
          },
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        "& .MuiButton-root": {
          backgroundColor: "#F70083",
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box width={"100%"} mb={8}>
        <NavBar />
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Card sx={{ minWidth: 275 }}>
          <CardContent>
            <FiltersContainer theme={theme}>
              <DatePicker
                label="Fecha Inicial"
                value={dayjs(filtersState.dayFrom)}
                format="YYYY/MM/DD"
                slotProps={{
                  textField: {
                    size: "small",
                  },
                }}
                onChange={(newValue) =>
                  handleInputChange(newValue, "SET_DAY_FROM")
                }
              />
              <DatePicker
                label="Fecha Final"
                value={dayjs(filtersState.dayTo)}
                format="YYYY/MM/DD"
                slotProps={{
                  textField: {
                    size: "small",
                  },
                }}
                onChange={(newValue) =>
                  handleInputChange(newValue, "SET_DAY_TO")
                }
              />
            </FiltersContainer>
            <TotalsCardsContainer theme={theme}>
              <StatisticCard
                title="Código Postal Más Buscado"
                value={topZipCodes.length !== 0 ? topZipCodes[0].codigoPostal : "-"}
                color={colors[0]}
              />
              <StatisticCard
                title="Oferta Más Clickeada"
                value={ topOffer ? `${topOffer.classification.name} - ${topOffer.play.name} - ${topOffer.internetPlan ? topOffer.internetPlan.name : topOffer.mobilePlan ? topOffer.mobilePlan.name : topOffer.videoPlan?.name  }` : "-"}
                color={colors[1]}
              />
              <StatisticCard
                title="Total de eventos"
                value={totalEvents.reduce(
                  (accumulator, currentValue) =>
                    accumulator + currentValue.totalEventos,
                  0
                )}
                color={colors[2]}
              />
            </TotalsCardsContainer>
            <InfoContainer theme={theme}>
              <MaterialReactTable table={table} />
              <Box maxWidth={"100%"}>
                <HighchartsReact highcharts={Highcharts} options={playEventsChart} />
                <HighchartsReact highcharts={Highcharts} options={totalEventsChart} />
                <HighchartsReact highcharts={Highcharts} options={mostSearchedZips} />
              </Box>
            </InfoContainer>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default Statistics;
